import angular from 'angular';
import voidEntryDialogTemplate from './VoidEntryDialog.template.html';
import CSMCommonWeb from 'csm-common-web';
import BooksServices from '../../booksServices';

export default angular
  .module('csm.books.com.VoidEntryDialog', [
    BooksServices,
    CSMCommonWeb,
    'ui.bootstrap'
  ])

  .service('VoidEntryDialog', VoidEntryDialogService).name;

/*******************************************************************************
 * Service: VoidEntryDialog
 *
 * Utility to show a dialog to manage voided and voiding ledger entry records.
 *******************************************************************************/
/* @ngInject */ function VoidEntryDialogService($uibModal, $filter) {
  /*****************************************************************************
   * Status Constants
   *****************************************************************************/
  this.STATUS_VOID = 'ENTRY_VOIDED';
  this.STATUS_UNVOID = 'ENTRY_UNVOIDED';

  /** Instance Reference. */ var mSelf = this;

  /*****************************************************************************
   * show */
  /**
   * Show the modal dialog for reviewing and managing void ledger entries. The
   * functionality of the dialog can be controlled by properties provided in the
   * options parameter. This function returns a promise to listen to when the
   * dialog is finished. On a success, the promise will resolve with a status
   * constant for the action performed.
   *
   * @param entry      Required ledger entry to void/review void
   * @param voidMethod Service method to call to save the void. This is expected
   *                   to take the void entry object as a parameter and return
   *                   a promise.
   * @param options    Values that control the functioning of the dialog. Valid
   *                   properties are:
   *                     allowUnvoid  - Allow for the unvoiding of a voided entry.
   *                                    Defaults to true.
   *                     description  - Description to add to the title bar
   *                     restrictDate - Restrict the void date to the entry date
   *****************************************************************************/
  this.show = function (entry, voidMethod, options) {
    if (!angular.isFunction(voidMethod))
      throw new Error('No void method has been provided');

    /*------------------------*/
    /* Ensure options are set */
    /*------------------------*/
    if (!options) options = {};

    return $uibModal.open({
      template: voidEntryDialogTemplate,
      size: 'md',
      backdrop: 'static',
      controller: function (
        $scope,
        $uibModalInstance,
        BooksServices,
        BusyScreen,
        Alert
      ) {
        /** Is Voided Indicator. */ var mIsVoided = false;
        /** Today's Date. */ $scope.today = $filter('date')(
          new Date(),
          'MM/dd/yyyy'
        );
        /** Date Validator. */ $scope.validators = {};

        /** Returns an indication that a void be undone/dropped by looking at the provided option value. */
        function canUnvoid() {
          /*
           * !!! Below is an abnormal occurrence. You shouldn't be allowed to void something that's cleared,
           * !!! OR clear something that's voided. This should happen is from a bug that would need to be fixed, or IT error.
           *
           * If the check has been voided, but later gets cleared, they should
           * not have the ability to unvoid the check if the void date ($scope.entry.entryDate)
           * is older than the reconciliation date (options.lastReconciledDate).
           *
           * Ex: options.lastReconciledDate (Jul 31 2022) and $scope.entry.entryDate (Sep 19 2019).
           * Jul 31 2022 >= Sep 19 2019 since $scope.entry.entryDate is older (less than) we would
           * not allow them to unvoid.
           *
           * Ex: options.lastReconciledDate (Jul 31 2022) and $scope.entry.entryDate (Jul 31 2022).
           * The situation goes like so, a user voids a check, ends up reconciliation the same day,
           * but then realizes it was a mistake to void and tries to unvoid the check. According to
           * Monica we do not want the User to be able to unvoid. We want the user to separately go
           * and un-reconcile, unvoid the check, then re-reconcile. Therefore, as long as the
           * lastReconciledDate is greater than OR equal to the entryDate, we do not allow them to unvoid.
           *
           * The reason we pass in the lastReconciledDate within the options object of the
           * "Write New Check" screen instead of sending in the allowUnvoid value set to
           * true is because the date of the void ($scope.entry.entryDate), is queried for
           * in here. Since this is a reused component if we wanted to change that it
           * would require a change in other files that utilize this component. So it
           * made more sense to bring in the reconciled date as an option and then do the
           * check here.
           *
           * For more information on the business rule please look up this article:
           * https://graystep.atlassian.net/wiki/spaces/BR/pages/1702232071/Voiding+Unvoiding+-+Checks+Receipts
           *
           * Ticket: BOOKS-475
           */
          if (
            options.lastReconciledDate &&
            $scope.entry.entryDate &&
            new Date(options.lastReconciledDate) >=
              new Date($scope.entry.entryDate)
          )
            return false;

          return (
            options.allowUnvoid == undefined || options.allowUnvoid == true
          );
        }

        /***********************************************************************
         * commit */
        /**
         * Commit button handler. Ensure the data is valid before performing the
         * available action. The modal is resolved with the appropriate status
         * constant.
         ***********************************************************************/
        $scope.commit = function () {
          /*-----------------------------------*/
          /* Clear any potential server errors */
          /*-----------------------------------*/
          Alert.clear();

          /*-----------------*/
          /* Validate Inputs */
          /*-----------------*/
          if (!$scope.validators.validate()) return;

          /*-------------------*/
          /* Unvoid if allowed */
          /*-------------------*/
          if (mIsVoided && canUnvoid()) {
            BusyScreen.setBusy(true);
            BooksServices.Voids.dropVoidEntry($scope.entry.referenceID).then(
              function () {
                BusyScreen.setBusy(false);
                $uibModalInstance.close(mSelf.STATUS_UNVOID);
              },
              function (resp) {
                BusyScreen.setBusy(false);
                Alert.error(
                  resp.parsedResponse.message() || resp.statusText,
                  'Unvoid Error'
                );
              }
            );
          } else if (!mIsVoided) {
            /*-----------------*/
            /* Attempt voiding */
            /*-----------------*/
            BusyScreen.setBusy(true);
            voidMethod($scope.entry).then(
              function () {
                BusyScreen.setBusy(false);
                $uibModalInstance.close(mSelf.STATUS_VOID);
              },

              /*--------------*/
              /* Server error */
              /*--------------*/
              function (resp) {
                BusyScreen.setBusy(false);
                Alert.error(
                  resp.parsedResponse.message() || resp.statusText,
                  'Void Error',
                  true
                );
              }
            );
          }
        };

        /** Disable the date on voided entries or when the date has been restricted. */
        $scope.disableDate = function () {
          return $scope.hasVoid() || options.restrictDate;
        };

        /** Returns the commit button label. */
        $scope.getCommitLabel = function () {
          return $scope.hasVoid() ? 'Unvoid' : 'Void';
        };

        /** Returns the dialog title based on the available action and the provided description. */
        $scope.getTitle = function () {
          return (
            ($scope.hasVoid() && canUnvoid() ? 'Unvoid ' : 'Void ') +
            (options.description || 'Entry')
          ).trim();
        };

        /** Pass-through to mIsVoided. */
        $scope.hasVoid = function () {
          return mIsVoided;
        };

        /** Show the commit button when not voided or when a void can be unvoided. */
        $scope.showCommit = function () {
          return !$scope.hasVoid() || canUnvoid();
        };

        /*-------------------------------------------------*/
        /* Set the minimum date to the provided entry date */
        /*-------------------------------------------------*/
        $scope.minDate = $filter('date')(entry.entryDate, 'MM/dd/yyyy');

        BusyScreen.setBusy(true);
        /*-------------------------------------------*/
        /* Attempt to load the void entry if it      */
        /* exists, otherwise start a new void entry. */
        /*-------------------------------------------*/
        BooksServices.Voids.getVoidEntry(entry.id).then(function (data) {
          BusyScreen.setBusy(false);
          mIsVoided = !!data;
          $scope.entry = data || {
            referenceID: entry.id,
            entryDate: options.restrictDate ? entry.entryDate : new Date()
          };
        });

        /*---------------------------------------------------------------*/
        /* When the dialog is removed, clear any lingering notifications */
        /*---------------------------------------------------------------*/
        $uibModalInstance.result.finally(Alert.clear);
      }
    }).result;
  };
}
